import type { RouteLocationNormalizedLoaded, Router } from "vue-router";
import { useAuthStore } from "@/store/auth";
import { resolveClubRouteContext } from "@/core/utils/clubRouteContext";
import type { RestSubscriptionStatus } from "@/types/subscription";

import { getLogger } from "@/utils/logger";
import { clearSessionId, getSessionId, SESSION_ID_STORAGE_KEY } from "@/utils/sessionIdTracker";

const logger = getLogger("UsageAnalytics");

type UsageEventName = "app_route_viewed" | "docs_link_clicked" | "subscription_cta_clicked" | "paywall_viewed";

type SubscriptionStatusOrigin = "cached" | "fresh";

type DocsClickSource = "main_app_bar" | "meeting_menu" | "club_workspace_help" | "help_icon";
type SubscriptionCtaSource =
  | "workspace_banner"
  | "inactive_page"
  | "feature_preview"
  | "subscription_page"
  | "paywall"
  | "club_home";
type PaywallVariant = "officer" | "member";

interface PendingUsageEvent {
  eventName: UsageEventName;
  occurredAt?: string;
  sessionId?: string;
  routeName?: string;
  featureArea?: string;
  clubId?: number;
  subscriptionStatus?: RestSubscriptionStatus;
  subscriptionStatusOrigin?: SubscriptionStatusOrigin;
  propertiesJson?: string;
}

interface ClubContext {
  clubId: number;
  clubShortName: string;
  subscriptionStatus?: RestSubscriptionStatus;
  subscriptionStatusOrigin?: SubscriptionStatusOrigin;
}

interface RouteSnapshot {
  routeName: string;
  featureArea: string;
  path: string;
  clubId?: number;
  subscriptionStatus?: RestSubscriptionStatus;
  subscriptionStatusOrigin?: SubscriptionStatusOrigin;
}

const CLUB_CONTEXT_KEY = "toasthost:usage-analytics-club-context";
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 20;

class UsageAnalyticsClient {
  private initialized = false;
  private queue: PendingUsageEvent[] = [];
  private flushTimer: number | null = null;
  private currentRoute: RouteSnapshot | null = null;
  private authGeneration = 0;

  initialize(router: Router): void {
    if (this.initialized) return;
    this.initialized = true;

    router.afterEach((to) => {
      void this.trackRouteView(to);
    });

    window.addEventListener("pagehide", () => this.flush({ useBeacon: true }));
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        this.flush({ useBeacon: true });
      }
    });
  }

  async trackRouteView(route: RouteLocationNormalizedLoaded): Promise<void> {
    if (!this.shouldTrack()) return;

    const routeName = this.getRouteName(route);
    const featureArea = this.getFeatureArea(route);
    const clubContext = this.resolveClubContext(route, featureArea);

    const nextRoute: RouteSnapshot = {
      routeName,
      featureArea,
      path: route.fullPath,
      clubId: clubContext?.clubId,
      subscriptionStatus: clubContext?.subscriptionStatus,
      subscriptionStatusOrigin: clubContext?.subscriptionStatusOrigin,
    };

    if (this.isSameTrackedRoute(this.currentRoute, nextRoute)) {
      return;
    }

    this.currentRoute = nextRoute;

    this.enqueue({
      eventName: "app_route_viewed",
      routeName,
      featureArea,
      clubId: clubContext?.clubId,
      subscriptionStatus: clubContext?.subscriptionStatus,
      subscriptionStatusOrigin: clubContext?.subscriptionStatusOrigin,
      propertiesJson: JSON.stringify({
        path: route.fullPath,
      }),
    });
  }

  trackDocsLinkClicked(source: DocsClickSource, targetUrl: string): void {
    if (!this.shouldTrack()) return;

    const context = this.getCurrentContext();
    this.enqueue({
      eventName: "docs_link_clicked",
      routeName: context.routeName,
      featureArea: context.featureArea,
      clubId: context.clubId,
      subscriptionStatus: context.subscriptionStatus,
      subscriptionStatusOrigin: context.subscriptionStatusOrigin,
      propertiesJson: JSON.stringify({
        source,
        targetUrl,
      }),
    });
  }

  trackSubscriptionCtaClicked(source: SubscriptionCtaSource, ctaAction: string): void {
    if (!this.shouldTrack()) return;

    const context = this.getCurrentContext();
    this.enqueue({
      eventName: "subscription_cta_clicked",
      routeName: context.routeName,
      featureArea: context.featureArea,
      clubId: context.clubId,
      subscriptionStatus: context.subscriptionStatus,
      subscriptionStatusOrigin: context.subscriptionStatusOrigin,
      propertiesJson: JSON.stringify({
        source,
        ctaAction,
      }),
    });
  }

  trackPaywallViewed(variant: PaywallVariant): void {
    if (!this.shouldTrack()) return;

    const context = this.getCurrentContext();
    this.enqueue({
      eventName: "paywall_viewed",
      routeName: context.routeName,
      featureArea: context.featureArea,
      clubId: context.clubId,
      subscriptionStatus: context.subscriptionStatus,
      subscriptionStatusOrigin: context.subscriptionStatusOrigin,
      propertiesJson: JSON.stringify({ variant }),
    });
  }

  resetForAuthTransition(options?: { resetSessionId?: boolean }): void {
    this.authGeneration += 1;
    this.queue = [];
    this.currentRoute = null;
    this.clearFlushTimer();
    sessionStorage.removeItem(CLUB_CONTEXT_KEY);

    // Per issue #1447 the cross-request session id deliberately persists
    // across login/logout — it's a per-browser-journey id, not an
    // authenticated-session token. The option is honoured only when
    // tests explicitly want a clean slate.
    if (options?.resetSessionId) {
      clearSessionId();
      try {
        localStorage.removeItem(SESSION_ID_STORAGE_KEY);
      } catch {
        /* localStorage may be unavailable in private mode */
      }
    }
  }

  async flush(options?: { useBeacon?: boolean }): Promise<void> {
    if (this.queue.length === 0) return;

    const flushGeneration = this.authGeneration;
    const events = [...this.queue];
    this.queue = [];
    this.clearFlushTimer();

    const payload = JSON.stringify({
      events: events.map((event) => {
        const { subscriptionStatusOrigin: _subscriptionStatusOrigin, ...payloadEvent } = event;
        return {
          ...payloadEvent,
          occurredAt: event.occurredAt ?? new Date().toISOString(),
          sessionId: event.sessionId ?? this.getSessionId(),
        };
      }),
    });

    if (options?.useBeacon && navigator.sendBeacon) {
      const sent = navigator.sendBeacon("/api/analytics/events", new Blob([payload], { type: "application/json" }));
      if (sent) return;
    }

    try {
      const response = await fetch("/api/analytics/events", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "same-origin",
        keepalive: options?.useBeacon === true,
        body: payload,
      });

      if (!response.ok) {
        if (this.shouldRetryStatus(response.status)) {
          if (this.canRequeueEvents(flushGeneration)) {
            this.requeueEvents(events);
            // Requeued, so nothing is lost and a retry is already scheduled:
            // warn rather than error, which reads as a failure that needs
            // attention. The error levels below mean events were dropped.
            logger.warn(`Retrying usage analytics flush after HTTP ${response.status}`);
          } else {
            logger.error(`Dropping usage analytics events after auth transition during HTTP ${response.status} retry`);
          }
        } else {
          logger.error(`Dropping usage analytics events after non-retryable HTTP ${response.status}`);
        }
      }
    } catch (error) {
      if (this.canRequeueEvents(flushGeneration)) {
        this.requeueEvents(events);
        // Transport failure (offline, a network change): requeued and retried,
        // so this is not an error anyone acts on.
        logger.warn("Failed to flush usage analytics events; will retry:", error);
      } else {
        logger.error("Dropping usage analytics events after auth transition during failed flush:", error);
      }
    }
  }

  setClubSubscriptionStatus(
    clubId?: number,
    clubShortName?: string,
    subscriptionStatus?: RestSubscriptionStatus | null,
  ): void {
    if (!clubId || !clubShortName || !subscriptionStatus) return;

    const existing = this.getStoredClubContext();
    const mergedContext: ClubContext =
      existing && existing.clubId === clubId
        ? { ...existing, clubId, clubShortName, subscriptionStatus }
        : { clubId, clubShortName, subscriptionStatus };

    this.storeClubContext(mergedContext);

    if (this.currentRoute?.clubId === clubId) {
      if (this.currentRoute.subscriptionStatusOrigin !== "fresh") {
        this.currentRoute = {
          ...this.currentRoute,
          subscriptionStatus,
          subscriptionStatusOrigin: "fresh",
        };
      }
    }

    this.queue = this.queue.map((event) => {
      if (event.clubId === clubId && event.subscriptionStatusOrigin !== "fresh") {
        return {
          ...event,
          subscriptionStatus,
          subscriptionStatusOrigin: "fresh",
        };
      }
      return event;
    });
  }

  resetForTests(): void {
    this.initialized = false;
    this.resetForAuthTransition({ resetSessionId: true });
  }

  private shouldTrack(): boolean {
    const authStore = useAuthStore();
    return Boolean(authStore.initialAuthCheckDone && authStore.isAuthenticated && authStore.user?.id);
  }

  private enqueue(event: Omit<PendingUsageEvent, "occurredAt" | "sessionId">): void {
    this.queue.push({
      ...event,
      occurredAt: new Date().toISOString(),
      sessionId: this.getSessionId(),
    });

    if (this.queue.length >= MAX_BATCH_SIZE) {
      void this.flush();
      return;
    }

    this.scheduleFlush();
  }

  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = window.setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, FLUSH_INTERVAL_MS);
  }

  private clearFlushTimer(): void {
    if (this.flushTimer !== null) {
      window.clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private getCurrentContext(): RouteSnapshot {
    const storedClubContext = this.getStoredClubContext();
    return (
      this.currentRoute ?? {
        routeName: "UnknownRoute",
        featureArea: "other",
        path: window.location.pathname + window.location.search,
        clubId: storedClubContext?.clubId,
        subscriptionStatus: storedClubContext?.subscriptionStatus,
        subscriptionStatusOrigin: storedClubContext?.subscriptionStatusOrigin,
      }
    );
  }

  private resolveClubContext(route: RouteLocationNormalizedLoaded, featureArea: string): ClubContext | undefined {
    const storedClubContext = this.getStoredClubContext();
    const routeClubShortName = typeof route.params.clubShortName === "string" ? route.params.clubShortName : undefined;
    if (routeClubShortName) {
      const authStore = useAuthStore();
      const resolvedClubContext = resolveClubRouteContext(routeClubShortName, authStore.user);
      const clubContext = resolvedClubContext
        ? {
            clubId: resolvedClubContext.clubId,
            clubShortName: resolvedClubContext.clubShortName,
            subscriptionStatus:
              storedClubContext?.clubId === resolvedClubContext.clubId
                ? storedClubContext.subscriptionStatus
                : undefined,
            subscriptionStatusOrigin:
              storedClubContext?.clubId === resolvedClubContext.clubId
                ? storedClubContext.subscriptionStatusOrigin
                : undefined,
          }
        : undefined;

      if (clubContext) {
        this.storeClubContext(clubContext);
      }

      return clubContext;
    }

    if (featureArea === "faq" || featureArea === "privacy" || featureArea === "docs" || featureArea === "profile") {
      return storedClubContext;
    }

    return undefined;
  }

  private getRouteName(route: RouteLocationNormalizedLoaded): string {
    if (typeof route.name === "string" && route.name.length > 0) return route.name;

    const path = route.path;
    if (path.startsWith("/admin/usage-analytics")) return "AdminUsageAnalytics";
    if (path.startsWith("/admin")) return "Admin";
    if (path === "/faq/" || path === "/faq") return "FaqPage";
    if (path === "/privacy/" || path === "/privacy") return "PrivacyPage";
    if (path === "/home" || path === "/home/") return "UserHome";
    if (path.includes("/subscription")) return "ClubSubscription";
    if (path.includes("/meeting")) return "ClubMeetingView";

    return path.replace(/[^a-zA-Z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "UnknownRoute";
  }

  private getFeatureArea(route: RouteLocationNormalizedLoaded): string {
    const name = typeof route.name === "string" ? route.name : "";
    const path = route.path;

    // Before the meetings check, because the EasySpeak meetings page's path contains `/meeting`.
    if (
      name === "ClubEducation" ||
      name === "ClubMemberActivity" ||
      name === "ClubRoleHistory" ||
      name === "EasySpeakImportReview" ||
      name === "EasySpeakMeetingsBrowser"
    )
      return "education";
    if (name.startsWith("ClubMeeting") || path.includes("/meeting")) return "meetings";
    if (name === "ClubMembership" || path.includes("/membership")) return "membership";
    if (name === "GuestbookManagement" || path.includes("guestbook")) return "guestbook";
    if (name === "WebsiteConfig") return "website";
    if (name === "ClubSettings" || name === "PeriodicEmailPreview") return "settings";
    if (name === "ClubSubscription" || path.includes("/subscription")) return "subscription";
    if (name === "UserProfile" || path.includes("/profile")) return "profile";
    if (path === "/faq/" || path === "/faq") return "faq";
    if (path === "/privacy/" || path === "/privacy") return "privacy";
    if (path.startsWith("/admin")) return "admin";
    if (name === "ClubHome" || path === "/home" || path === "/home/") return "home";
    return "other";
  }

  private getSessionId(): string {
    // Issue #1447: pull the same per-browser sid the request interceptor
    // sends as `X-Session-Id` so analytics events join on one identifier
    // with `backend-dev.log`. Previously this lived in sessionStorage
    // under a separate key — that meant analytics events had a different
    // sid from request log lines, defeating the point of correlation.
    return getSessionId();
  }

  private storeClubContext(context: ClubContext): void {
    sessionStorage.setItem(CLUB_CONTEXT_KEY, JSON.stringify(context));
  }

  private requeueEvents(events: PendingUsageEvent[]): void {
    this.queue = [...events, ...this.queue];
    this.scheduleFlush();
  }

  private canRequeueEvents(flushGeneration: number): boolean {
    return flushGeneration === this.authGeneration;
  }

  private shouldRetryStatus(status: number): boolean {
    return status === 429 || status >= 500;
  }

  private isSameTrackedRoute(left: RouteSnapshot | null, right: RouteSnapshot): boolean {
    return (
      left !== null && left.routeName === right.routeName && left.path === right.path && left.clubId === right.clubId
    );
  }

  private getStoredClubContext(): ClubContext | undefined {
    const raw = sessionStorage.getItem(CLUB_CONTEXT_KEY);
    if (!raw) return undefined;

    try {
      const parsed = JSON.parse(raw) as ClubContext;
      if (
        typeof parsed.clubId === "number" &&
        typeof parsed.clubShortName === "string" &&
        (parsed.subscriptionStatus === undefined || this.isSubscriptionStatus(parsed.subscriptionStatus))
      ) {
        return {
          ...parsed,
          subscriptionStatusOrigin: parsed.subscriptionStatus ? "cached" : undefined,
        };
      }
    } catch {
      sessionStorage.removeItem(CLUB_CONTEXT_KEY);
    }

    return undefined;
  }

  private isSubscriptionStatus(value: unknown): value is RestSubscriptionStatus {
    return (
      value === "not_subscribed" ||
      value === "trial" ||
      value === "active" ||
      value === "cancelling" ||
      value === "grace_period" ||
      value === "cancelled"
    );
  }
}

export const usageAnalytics = new UsageAnalyticsClient();
